import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuthStore } from '@/stores/auth-store';
import { setUnauthorizedHandler } from '@/services/api-client';
import { isBiometricEnabled, verifyBiometric } from '@/services/biometric';
import { isNative } from '@/services/platform';

interface AuthGateProps {
  children: ReactNode;
  /** When true, page is for guests only (login). Authed users get bounced home. */
  guestOnly?: boolean;
}

type BioState = 'unknown' | 'pass' | 'prompt' | 'fail';

/** Wraps children to enforce auth status. Triggers bootstrap on first mount. */
export function AuthGate({ children, guestOnly = false }: AuthGateProps) {
  const status = useAuthStore((s) => s.status);
  const bootstrap = useAuthStore((s) => s.bootstrap);
  const markUnauthenticated = useAuthStore((s) => s.markUnauthenticated);
  const location = useLocation();
  const [bio, setBio] = useState<BioState>('unknown');

  useEffect(() => {
    if (status === 'unknown') void bootstrap();
  }, [status, bootstrap]);

  useEffect(() => {
    setUnauthorizedHandler(() => markUnauthenticated());
  }, [markUnauthenticated]);

  useEffect(() => {
    if (guestOnly || status !== 'authed') return;
    if (!isNative()) {
      setBio('pass');
      return;
    }
    let cancelled = false;
    void (async () => {
      const enabled = await isBiometricEnabled();
      if (cancelled) return;
      if (!enabled) {
        setBio('pass');
        return;
      }
      setBio('prompt');
      try {
        await verifyBiometric('Unlock GPL Pro');
        if (!cancelled) setBio('pass');
      } catch {
        if (!cancelled) setBio('fail');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [guestOnly, status]);

  if (status === 'unknown') {
    return <div className="auth-gate-loading" aria-busy="true" />;
  }

  if (guestOnly && status === 'authed') {
    return <Navigate to="/" replace />;
  }
  if (!guestOnly && status === 'guest') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (!guestOnly && bio === 'prompt') {
    return <div className="auth-gate-loading" aria-busy="true" />;
  }
  if (!guestOnly && bio === 'fail') {
    return (
      <div className="auth-gate-bio-fail">
        <button
          type="button"
          onClick={() => {
            setBio('prompt');
            void verifyBiometric('Unlock GPL Pro')
              .then(() => setBio('pass'))
              .catch(() => setBio('fail'));
          }}
        >
          Retry unlock
        </button>
      </div>
    );
  }

  return <>{children}</>;
}
